import type { SowPulseSubscriptionSeatSummary, SowSummary } from '../../../services/api';
import { formatInclusiveDate } from './dateDisplay';

/** ISO timestamp for seat expiration at the end of the current pulse credit cycle. */
export function resolvePulseCreditCycleEndEffectiveAt(sow: SowSummary): string | null {
    const pulsePool = sow.balanceSummary?.pools?.find((pool) => pool.poolType === 'pulse_credit');
    if (pulsePool?.currentPeriodEnd) {
        return pulsePool.currentPeriodEnd;
    }
    return sow.endDate ?? null;
}

export function formatEffectiveAtLabel(effectiveAt: string | null): string {
    if (!effectiveAt) {
        return 'the end of the current cycle';
    }
    return formatInclusiveDate(effectiveAt);
}

export type PulseSeatLifecycle = 'active' | 'pending_downgrade' | 'expired';

export function getPulseSeatLifecycle(
    seat: Pick<SowPulseSubscriptionSeatSummary, 'activeToDate'>,
    referenceDate = new Date(),
): PulseSeatLifecycle {
    if (!seat.activeToDate) {
        return 'active';
    }
    const end = new Date(seat.activeToDate);
    if (Number.isNaN(end.getTime())) {
        return 'active';
    }
    return end > referenceDate ? 'pending_downgrade' : 'expired';
}

export function summarizePulseSeatLifecycles(
    seats: SowPulseSubscriptionSeatSummary[],
    referenceDate = new Date(),
): {
    pendingDowngradeCount: number;
    expiredCount: number;
    pendingDowngradeExpirationLabels: string[];
} {
    const expirationIsoDates = new Set<string>();
    let pendingDowngradeCount = 0;
    let expiredCount = 0;

    for (const seat of seats) {
        const lifecycle = getPulseSeatLifecycle(seat, referenceDate);
        if (lifecycle === 'pending_downgrade') {
            pendingDowngradeCount += 1;
            if (seat.activeToDate) {
                expirationIsoDates.add(seat.activeToDate);
            }
        } else if (lifecycle === 'expired') {
            expiredCount += 1;
        }
    }

    const pendingDowngradeExpirationLabels = [...expirationIsoDates]
        .sort()
        .map((isoDate) => formatInclusiveDate(isoDate));

    return {
        pendingDowngradeCount,
        expiredCount,
        pendingDowngradeExpirationLabels,
    };
}

export function pulseSeatLifecycleLabel(lifecycle: PulseSeatLifecycle): string {
    switch (lifecycle) {
        case 'active':
            return 'Active';
        case 'pending_downgrade':
            return 'Pending downgrade';
        case 'expired':
            return 'Expired';
    }
}

export interface PulseSeatInventorySummary {
    currentPurchasedCount: number;
    futurePurchasedCount: number;
    assignedCount: number;
    availableCount: number;
    pendingDowngradeCount: number;
    expiredCount: number;
    pendingDowngradeExpirationLabels: string[];
}

export interface PulseSeatQuantityChangeSummary {
    pendingSeatCount: number;
    seatsToExpireCount: number;
    autoExpiredSeatCount: number;
    occupiedSeatCountToExpire: number;
}

function isSeatActivePastEffectiveAt(
    seat: Pick<SowPulseSubscriptionSeatSummary, 'activeToDate'>,
    effectiveAt: Date,
): boolean {
    if (!seat.activeToDate) {
        return true;
    }
    const activeToDate = new Date(seat.activeToDate);
    if (Number.isNaN(activeToDate.getTime())) {
        return true;
    }
    return activeToDate > effectiveAt;
}

export function summarizePulseSeatQuantityChange(
    seats: SowPulseSubscriptionSeatSummary[],
    targetSeatCount: number,
    effectiveAtIso: string,
): PulseSeatQuantityChangeSummary {
    const effectiveAt = new Date(effectiveAtIso);
    if (Number.isNaN(effectiveAt.getTime()) || !Number.isInteger(targetSeatCount)) {
        return {
            pendingSeatCount: 0,
            seatsToExpireCount: 0,
            autoExpiredSeatCount: 0,
            occupiedSeatCountToExpire: 0,
        };
    }

    const pendingSeats = seats.filter((seat) => isSeatActivePastEffectiveAt(seat, effectiveAt));
    const pendingSeatCount = pendingSeats.length;
    const seatsToExpireCount = Math.max(pendingSeatCount - targetSeatCount, 0);
    const openPendingSeatCount = pendingSeats.filter(
        (seat) => seat.inUse !== true || seat.userId == null,
    ).length;
    const autoExpiredSeatCount = Math.min(seatsToExpireCount, openPendingSeatCount);

    return {
        pendingSeatCount,
        seatsToExpireCount,
        autoExpiredSeatCount,
        occupiedSeatCountToExpire: Math.max(seatsToExpireCount - autoExpiredSeatCount, 0),
    };
}

/** Derive seat counts from seat rows — not subscription.numSeats. */
export function summarizePulseSeatInventory(
    seats: SowPulseSubscriptionSeatSummary[],
    referenceDate = new Date(),
): PulseSeatInventorySummary {
    const lifecycleSummary = summarizePulseSeatLifecycles(seats, referenceDate);
    let currentPurchasedCount = 0;
    let futurePurchasedCount = 0;
    let assignedCount = 0;

    for (const seat of seats) {
        const lifecycle = getPulseSeatLifecycle(seat, referenceDate);
        if (lifecycle === 'expired') {
            continue;
        }
        currentPurchasedCount += 1;
        if (lifecycle === 'active') {
            futurePurchasedCount += 1;
        }
        if (seat.userId != null && seat.userId !== undefined) {
            assignedCount += 1;
        }
    }

    return {
        ...lifecycleSummary,
        currentPurchasedCount,
        futurePurchasedCount,
        assignedCount,
        availableCount: Math.max(currentPurchasedCount - assignedCount, 0),
    };
}
